import { useContext, useState } from "react";
import { Context } from "../../../../context/Context";
import { useRestaurantAccess } from "../../../../access/hooks/useRestaurantAccess";
import { DeleteModal } from "../../../modals/Delete";
import { translations } from "../../../../utils/lang";
import "./MonthlyExpand.css";
import { MONTHLY_FIELDS2 } from "../../../../utils/Monthly";
import { normalizeDeliveryAppKey } from "../../../../utils/integrations";
import {
  deleteMonthlyAPI,
  updateApprovalByStoreDateAPI,
  updateDisapproveByStoreDateAppAPI,
  updateMonthlyAPI,
  updateMonthlyByStoreDateAppAPI,
} from "../../../../utils/api/report";
import {
  createGrantTotalPercent,
  createServiceChargePercent,
  formatter,
  formatter2,
} from "../../../../utils/helpers";
import { Button, Repeat, ThumbsDown, ThumbsUp, Trash2 } from "../../../ui";
import {
  groupBy,
  groupTotals,
  pendingMarker,
  rowMonthKey,
  rowToggleProps,
  sortMonthEntries,
  stopRowToggle,
  storeIdsOf,
} from "../../reportUtils";
import { NodeState } from "../../reportNode";
import { getFieldLabel, getReportText } from "../../reportText";
import { MONTHLY_FIELDS as AR_FIELD_LABELS } from "../../../../utils/Monthly";

const SUM_KEYS = [
  "completed", "completedAmount", "rejected", "canceled", "rejectedAmount", "canceledAmount", "endurance", "enduranceAmount", "refund", "refundAmount",
  "serviceCharge", "deliveryOffer", "keetaDeliveryOffer", "offers", "paidCampains", "ccAmount", "vat", "grandTotal",
];

// `groups` (optional): server-side totals per month / app for the store's whole filtered set. The headers print those instead of summing
// the rows they were given. Without `groups` (older backend) the headers sum `filteredData`.
// `paged` (optional, the server-paged table): `filteredData` then only carries the hierarchy levels (usePagedReport), the branch rows of the open month
// arrive in `paged.leafRows`, and opening a month reports it through `paged.onNode`.
export const MonthlyExpand = ({
  filteredData = [],
  onRefresh,
  groups = null,
  paged = null,
}) => {
  const { lang, handleTokenExpiration } = useContext(Context);
  const { can, has, canForAll } = useRestaurantAccess();
  const [expandedDate, setExpandedDate] = useState(null);
  const [expandedApp, setExpandedApp] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editItem, setEditItem] = useState({});
  // One mutation at a time; failures are shown instead of swallowed.
  const [pending, setPending] = useState(false);
  const [actionError, setActionError] = useState("");
  const text = getReportText(lang);
  const maps = {
    app: translations[lang].filter.apps,
  };

  // Group by date
  // Monthly dates are already "MonthName-YYYY".
  const groupedByDate = groupBy(
    filteredData,
    (item) => item[MONTHLY_FIELDS2.date] || "Unknown Date",
  );

  // Keyed by the record id: the same branch name appears under every month
  // and app, and keying by name let a save hit a different record.
  const handleEdit = (item) => {
    setIsEditing(item._id);
    setEditItem(item);
  };
  const handleCancel = () => {
    setIsEditing(null);
    setEditItem({});
  };

  const handleChange = (field, value) => {
    setEditItem((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleDate = (date, items) => {
    paged?.onNode(expandedDate === date ? {} : { month: rowMonthKey(items[0], "monthly") });
    setExpandedDate((prev) => (prev === date ? null : date));
    setExpandedApp(null);
  };

  const toggleApp = (app) => {
    setExpandedApp((prev) => (prev === app ? null : app));
  };
  const confirmDelete = (id) => {
    setDeleteTarget(id);
  };

  // Runs a mutation; the API helpers resolve to undefined on failure.
  const runMutation = async (request, onSuccess) => {
    if (pending) return false;
    const token = localStorage.getItem("auth-token");
    if (!token) {
      handleTokenExpiration();
      return false;
    }
    setPending(true);
    setActionError("");
    try {
      const result = await request(token);
      if (!result) {
        setActionError(text.actionFailed);
        return false;
      }
      onSuccess?.();
      onRefresh?.(); // refresh parent
      return true;
    } catch (err) {
      console.error("Monthly report update failed:", err);
      setActionError(text.actionFailed);
      return false;
    } finally {
      setPending(false);
    }
  };

  const handleConfirmedDeleteRaw = (id) =>
    runMutation(
      (token) => deleteMonthlyAPI(token, id),
      () => setDeleteTarget(null),
    );

  // Saves the record that is being edited (not whichever row is rendered).
  const handleSaveRaw = () =>
    runMutation(
      (token) => updateMonthlyAPI(token, editItem._id, editItem),
      () => {
        setIsEditing(null);
        setEditItem({});
      },
    );

  const approveDataRaw = (store, date, app, storeIds) =>
    runMutation((token) =>
      updateMonthlyByStoreDateAppAPI(token, store, date, app, storeIds),
    );

  const disapproveDataRaw = (store, date, app, storeIds) =>
    runMutation((token) =>
      updateDisapproveByStoreDateAppAPI(token, "monthly", store, date, app, storeIds),
    );

  const approveByMonthRaw = (store, date, approve, storeIds) =>
    runMutation((token) =>
      updateApprovalByStoreDateAPI(token, "monthly", store, date, approve, storeIds),
    );



  // Handler-level authorization: re-checked right before every request, against
  // the restaurant(s) the record belongs to (report rows carry the restaurant _id,
  // resolved to the public id by useRestaurantAccess). Hidden buttons are not enough.
  const refusedMutation = Promise.resolve(false);
  const handleConfirmedDelete = (id) =>
    canForAll("report.entry.delete", [[...filteredData, ...(paged?.leafRows || [])].find((row) => row._id === id)?.storeId])
      ? handleConfirmedDeleteRaw(id)
      : refusedMutation;
  const handleSave = () =>
    canForAll("report.entry.update", [editItem?.storeId]) ? handleSaveRaw() : refusedMutation;
  const approveData = (store, date, app, storeIds) =>
    canForAll("report.approve", storeIds) ? approveDataRaw(store, date, app, storeIds) : refusedMutation;
  const disapproveData = (store, date, app, storeIds) =>
    canForAll("report.approve", storeIds) ? disapproveDataRaw(store, date, app, storeIds) : refusedMutation;
  const approveByMonth = (store, date, approve, storeIds) =>
    canForAll("report.approve", storeIds) ? approveByMonthRaw(store, date, approve, storeIds) : refusedMutation;


  return (
    <div className="monthly-expand-table">
      {actionError && (
        <p className="report-action-error" role="alert">
          {actionError}
        </p>
      )}
      {sortMonthEntries(groupedByDate.entries(), paged?.ascending)
        .map(([date, dateItems]) => {
          const apps = dateItems.reduce((acc, item) => {
            const app = item[MONTHLY_FIELDS2.app] || "Unknown App";
            acc[app] = acc[app] || [];
            acc[app].push(item);
            return acc;
          }, {});

          const dateSummary = dateItems.reduce(
            (acc, item) => {
              acc.completed += parseFloat(item[MONTHLY_FIELDS2.completed] || 0);
              acc.completedAmount += parseFloat(
                item[MONTHLY_FIELDS2.completedAmount] || 0,
              );
              acc.rejected += parseFloat(item[MONTHLY_FIELDS2.rejected] || 0);
              acc.canceled += parseFloat(item[MONTHLY_FIELDS2.canceled] || 0);
              acc.rejectedAmount += parseFloat(
                item[MONTHLY_FIELDS2.rejectedAmount] || 0,
              );
              acc.canceledAmount += parseFloat(
                item[MONTHLY_FIELDS2.canceledAmount] || 0,
              );
              acc.endurance += parseFloat(item[MONTHLY_FIELDS2.endurance] || 0);
              acc.enduranceAmount += parseFloat(
                item[MONTHLY_FIELDS2.enduranceAmount] || 0,
              );
              acc.refund += parseFloat(item[MONTHLY_FIELDS2.refund] || 0);
              acc.refundAmount += parseFloat(
                item[MONTHLY_FIELDS2.refundAmount] || 0,
              );
              acc.serviceCharge += parseFloat(
                item[MONTHLY_FIELDS2.serviceCharge] || 0,
              );
              acc.deliveryOffer += parseFloat(
                item[MONTHLY_FIELDS2.deliveryOffer] || 0,
              );
              acc.keetaDeliveryOffer +=
                normalizeDeliveryAppKey(item[MONTHLY_FIELDS2.app]) === "keeta"
                  ? parseFloat(item[MONTHLY_FIELDS2.deliveryOffer] || 0)
                  : 0;
              acc.offers += parseFloat(item.offers || 0);
              acc.paidCampains += parseFloat(item.paidCampains || 0);
              acc.ccAmount += parseFloat(item[MONTHLY_FIELDS2.ccAmount] || 0);
              acc.vat += parseFloat(item[MONTHLY_FIELDS2.vat] || 0);
              acc.grandTotal += parseFloat(
                item[MONTHLY_FIELDS2.grandTotal] || 0,
              );
              acc.notes += (item.notes ? " / " : "") + (item.notes || "");
              return acc;
            },
            {
              completed: 0,
              completedAmount: 0,
              rejected: 0,
              canceled: 0,
              rejectedAmount: 0,
              canceledAmount: 0,
              endurance: 0,
              enduranceAmount: 0,
              refund: 0,
              refundAmount: 0,
              serviceCharge: 0,
              deliveryOffer: 0,
              keetaDeliveryOffer: 0,
              offers: 0,
              paidCampains: 0,
              ccAmount: 0,
              vat: 0,
              grandTotal: 0,
              notes: "",
            },
          );
          const dateTotals = groupTotals(groups, { month: rowMonthKey(dateItems[0], "monthly") }, SUM_KEYS);
          if (dateTotals) Object.assign(dateSummary, dateTotals, { notes: dateTotals.notes.join(" / ") });
          const isMonthApproved = pendingMarker(dateTotals, dateItems.find((a) => a.isApproved === false));
          const [monthSplit, yearSplit] = date.split("-");
          return (
            <div key={date}>
              <div
                className={`report-monthly-summary-card  ${
                  isMonthApproved && !isMonthApproved?.isApproved
                    ? "notApproved"
                    : ""
                }`}
                {...rowToggleProps(expandedDate === date, () =>
                  toggleDate(date, dateItems),
                )}
              >
                {dateSummary.notes && (
                  <div className="monthly-expand-p all-cols">
                    {dateSummary.notes}
                  </div>
                )}
                <div className="monthly-expand-grid-col">
                  <div className="monthly-expand-p center">
                    <p>
                      <strong>
                        {translations[lang].filter.month[monthSplit]}-
                        {yearSplit}
                      </strong>
                    </p>
                  </div>
                </div>

                <div className="monthly-expand-grid-col">
                  <div className="monthly-expand-p">
                    <strong>
                      {translations[lang].reports.monthly.body[0]}
                    </strong>
                    {` ${formatter2.format(
                      dateSummary.completed,
                    )} / ${formatter.format(
                      dateSummary.completedAmount.toFixed(2),
                    )}`}
                  </div>
                  <div className="monthly-expand-p">
                    <strong>
                      {translations[lang].reports.monthly.body[1]}
                    </strong>
                    {formatter.format(dateSummary.vat.toFixed(2))}
                  </div>
                  <div className="monthly-expand-p">
                    <strong>
                      {translations[lang].reports.monthly.body[2]}
                    </strong>
                    (
                    {createGrantTotalPercent({
                      completedAmount: dateSummary.completedAmount,
                      grandTotal:
                        dateSummary.grandTotal - dateSummary.paidCampains,
                      offers: dateSummary.offers,
                    })}
                    %) /{" "}
                    {formatter.format(
                      dateSummary.grandTotal.toFixed(2) -
                        dateSummary.paidCampains.toFixed(2),
                    )}
                  </div>
                </div>

                <div className="monthly-expand-grid-col">
                  <div className="monthly-expand-p">
                    <div className="monthly-expand-p-grid">
                      <div>
                        <strong>
                          {translations[lang].reports.monthly.body[3]}
                        </strong>
                        {formatter2.format(dateSummary.rejected)} /{" "}
                        {formatter.format(dateSummary.rejectedAmount)}
                      </div>
                      <div>
                        <strong>
                          {translations[lang].reports.monthly.body[4]}
                        </strong>
                        {formatter2.format(dateSummary.canceled)} /{" "}
                        {formatter.format(dateSummary.canceledAmount)}
                      </div>
                    </div>
                  </div>
                  <div className="monthly-expand-p">
                    <div className="monthly-expand-p-grid">
                      <div>
                        <strong>
                          {translations[lang].reports.monthly.body[5]}
                        </strong>
                        {`${formatter2.format(
                          dateSummary.endurance,
                        )} / ${formatter.format(
                          dateSummary.enduranceAmount.toFixed(2),
                        )}`}
                      </div>
                      <div>
                        <strong>
                          {translations[lang].reports.monthly.body[6]}
                        </strong>
                        {`${formatter2.format(
                          dateSummary.refund,
                        )} / ${formatter.format(
                          dateSummary.refundAmount.toFixed(2),
                        )}`}
                      </div>
                    </div>
                  </div>
                  <div className="monthly-expand-p">
                    <strong>
                      {translations[lang].reports.monthly.body[9]}
                    </strong>
                    {formatter.format(dateSummary.ccAmount.toFixed(2))}
                  </div>
                </div>

                <div className="monthly-expand-grid-col">
                  <div className="monthly-expand-p">
                    <strong>
                      {translations[lang].reports.monthly.body[7]}
                    </strong>
                    (
                    {createServiceChargePercent({
                      completedAmount: dateSummary.completedAmount,
                      serviceCharge: dateSummary.serviceCharge,
                      offers: dateSummary.offers,
                      deliveryOffer: dateSummary.keetaDeliveryOffer,
                    })}
                    %) /{" "}
                    {formatter.format(dateSummary.serviceCharge.toFixed(2))}
                  </div>
                  <div className="monthly-expand-p">
                    <div className="monthly-expand-p-grid">
                      <div>
                        <strong>
                          {translations[lang].reports.monthly.body[8]}
                        </strong>
                        ({" "}
                        {dateSummary.completed > 0
                          ? (
                              dateSummary.deliveryOffer / dateSummary.completed
                            ).toFixed(2)
                          : 0}
                        ) /{" "}
                        {formatter.format(dateSummary.deliveryOffer.toFixed(2))}
                      </div>
                      <div>
                        <strong>
                          {translations[lang].reports.monthly.body[10]}
                        </strong>
                        (
                        {dateSummary.completed > 0
                          ? (
                              dateSummary.offers / dateSummary.completed
                            ).toFixed(2)
                          : 0}
                        ) / {formatter.format(dateSummary.offers.toFixed(2))}
                      </div>
                    </div>
                  </div>
                  <div className="monthly-expand-p">
                    <strong>
                      {translations[lang].reports.monthly.body[11]}
                    </strong>
                    {formatter.format(dateSummary.paidCampains.toFixed(2))}
                  </div>
                </div>
                {has("report.approve") &&
                  (isMonthApproved && !isMonthApproved?.isApproved ? (
                    <div>
                      <Button
                        variant="danger"
                        appearance="soft"
                        rounded
                        icon={<ThumbsUp size={20} />}
                        aria-label={text.approveMonth}
                        title={text.approveMonth}
                        disabled={pending}
                        onClick={stopRowToggle(() =>
                          approveByMonth(dateItems[0].storeName, dateItems[0].date, true, storeIdsOf(dateItems)),
                        )}
                      ></Button>
                    </div>
                  ) : (
                    <div>
                      <Button
                        variant="danger"
                        appearance="soft"
                        rounded
                        icon={<ThumbsDown size={20} />}
                        aria-label={text.disapproveMonth}
                        title={text.disapproveMonth}
                        disabled={pending}
                        onClick={stopRowToggle(() =>
                          approveByMonth(dateItems[0].storeName, dateItems[0].date, false, storeIdsOf(dateItems)),
                        )}
                      ></Button>
                    </div>
                  ))}
              </div>

              {expandedDate === date && paged?.detailPending && <NodeState state={paged.detail} />}
              {expandedDate === date && !paged?.detailPending && (
                <div className="expand-apps">
                  {Object.entries(apps).map(([app, appItems]) => {
                    const branches = (paged ? paged.leafRows.filter((row) => row.app === app) : appItems).reduce((acc, item) => {
                      const branch =
                        item[MONTHLY_FIELDS2.branchName] || "Unknown Branch";
                      acc[branch] = acc[branch] || [];
                      acc[branch].push(item);
                      return acc;
                    }, {});

                    const appSummary = appItems.reduce(
                      (acc, item) => {
                        acc.completed += parseFloat(
                          item[MONTHLY_FIELDS2.completed] || 0,
                        );
                        acc.completedAmount += parseFloat(
                          item[MONTHLY_FIELDS2.completedAmount] || 0,
                        );
                        acc.rejected += parseFloat(
                          item[MONTHLY_FIELDS2.rejected] || 0,
                        );
                        acc.canceled += parseFloat(
                          item[MONTHLY_FIELDS2.canceled] || 0,
                        );
                        acc.rejectedAmount += parseFloat(
                          item[MONTHLY_FIELDS2.rejectedAmount] || 0,
                        );
                        acc.canceledAmount += parseFloat(
                          item[MONTHLY_FIELDS2.canceledAmount] || 0,
                        );
                        acc.endurance += parseFloat(
                          item[MONTHLY_FIELDS2.endurance] || 0,
                        );
                        acc.enduranceAmount += parseFloat(
                          item[MONTHLY_FIELDS2.enduranceAmount] || 0,
                        );
                        acc.refund += parseFloat(
                          item[MONTHLY_FIELDS2.refund] || 0,
                        );
                        acc.refundAmount += parseFloat(
                          item[MONTHLY_FIELDS2.refundAmount] || 0,
                        );
                        acc.serviceCharge += parseFloat(
                          item[MONTHLY_FIELDS2.serviceCharge] || 0,
                        );
                        acc.deliveryOffer += parseFloat(
                          item[MONTHLY_FIELDS2.deliveryOffer] || 0,
                        );
                        acc.keetaDeliveryOffer +=
                          normalizeDeliveryAppKey(item[MONTHLY_FIELDS2.app]) === "keeta"
                            ? parseFloat(
                                item[MONTHLY_FIELDS2.deliveryOffer] || 0,
                              )
                            : 0;
                        acc.offers += parseFloat(item.offers || 0);
                        acc.paidCampains += parseFloat(item.paidCampains || 0);
                        acc.ccAmount += parseFloat(
                          item[MONTHLY_FIELDS2.ccAmount] || 0,
                        );
                        acc.vat += parseFloat(item[MONTHLY_FIELDS2.vat] || 0);
                        acc.grandTotal += parseFloat(
                          item[MONTHLY_FIELDS2.grandTotal] || 0,
                        );
                        acc.notes += item.notes || "";
                        return acc;
                      },
                      {
                        completed: 0,
                        completedAmount: 0,
                        rejected: 0,
                        canceled: 0,
                        rejectedAmount: 0,
                        canceledAmount: 0,
                        endurance: 0,
                        enduranceAmount: 0,
                        refund: 0,
                        refundAmount: 0,
                        serviceCharge: 0,
                        deliveryOffer: 0,
                        keetaDeliveryOffer: 0,
                        offers: 0,
                        paidCampains: 0,
                        ccAmount: 0,
                        vat: 0,
                        grandTotal: 0,
                        notes: "",
                      },
                    );
                    const appTotals = groupTotals(groups, { month: rowMonthKey(appItems[0], "monthly"), app: appItems[0].app ?? null }, SUM_KEYS);
                    if (appTotals) Object.assign(appSummary, appTotals, { notes: appTotals.notes.join(" / ") });
                    const isAppApproved = pendingMarker(
                      appTotals,
                      appItems.find((a) => a.isApproved === false),
                    );

                    return (
                      <div key={app} className="expand-app-section">
                        <div
                          className={`report-monthly-summary-card  ${
                            isAppApproved && !isAppApproved?.isApproved
                              ? "notApproved"
                              : ""
                          }`}
                          {...rowToggleProps(expandedApp === app, () =>
                            toggleApp(app),
                          )}
                        >
                          {appSummary.notes && (
                            <div className="monthly-expand-p all-cols">
                              {appSummary.notes}
                            </div>
                          )}

                          <div className="monthly-expand-grid-col">
                            <div className="monthly-expand-p center app">
                              <p>
                                <strong>
                                  {maps.app?.[app] ??
                                    translations[lang].notApp +
                                      " " +
                                      app[app.length - 1]}
                                </strong>
                              </p>
                            </div>
                          </div>

                          <div className="monthly-expand-grid-col">
                            <div className="monthly-expand-p">
                              <strong>
                                {translations[lang].reports.monthly.body[0]}
                              </strong>
                              {` ${formatter2.format(
                                appSummary.completed,
                              )} / ${formatter.format(
                                appSummary.completedAmount.toFixed(2),
                              )}`}
                            </div>
                            <div className="monthly-expand-p">
                              <strong>
                                {translations[lang].reports.monthly.body[1]}
                              </strong>
                              {formatter.format(appSummary.vat.toFixed(2))}
                            </div>
                            <div className="monthly-expand-p">
                              <strong>
                                {translations[lang].reports.monthly.body[2]}
                              </strong>
                              (
                              {createGrantTotalPercent({
                                completedAmount: appSummary.completedAmount,
                                grandTotal:
                                  appSummary.grandTotal -
                                  appSummary.paidCampains,
                                offers: appSummary.offers,
                              })}
                              %) /{" "}
                              {formatter.format(
                                appSummary.grandTotal.toFixed(2) -
                                  appSummary.paidCampains.toFixed(2),
                              )}
                            </div>
                          </div>

                          <div className="monthly-expand-grid-col">
                            <div className="monthly-expand-p">
                              <div className="monthly-expand-p-grid">
                                <div>
                                  <strong>
                                    {translations[lang].reports.monthly.body[3]}
                                  </strong>
                                  {formatter2.format(appSummary.rejected)} /{" "}
                                  {formatter.format(appSummary.rejectedAmount)}
                                </div>
                                <div>
                                  <strong>
                                    {translations[lang].reports.monthly.body[4]}
                                  </strong>
                                  {formatter2.format(appSummary.canceled)} /{" "}
                                  {formatter.format(appSummary.canceledAmount)}
                                </div>
                              </div>
                            </div>
                            <div className="monthly-expand-p">
                              <div className="monthly-expand-p-grid">
                                <div>
                                  <strong>
                                    {translations[lang].reports.monthly.body[6]}
                                  </strong>
                                  {`${formatter2.format(
                                    appSummary.refund,
                                  )} / ${formatter.format(
                                    appSummary.refundAmount.toFixed(2),
                                  )}`}
                                </div>
                              </div>
                              <strong>
                                {translations[lang].reports.monthly.body[5]}
                              </strong>
                              {`${formatter2.format(
                                appSummary.endurance,
                              )} / ${formatter.format(
                                appSummary.enduranceAmount.toFixed(2),
                              )}`}
                              <div></div>
                            </div>
                            <div className="monthly-expand-p">
                              <strong>
                                {translations[lang].reports.monthly.body[9]}
                              </strong>
                              {formatter.format(appSummary.ccAmount.toFixed(2))}
                            </div>
                          </div>

                          <div className="monthly-expand-grid-col">
                            <div className="monthly-expand-p">
                              <strong>
                                {translations[lang].reports.monthly.body[7]}
                              </strong>
                              (
                              {createServiceChargePercent({
                                completedAmount: appSummary.completedAmount,
                                serviceCharge: appSummary.serviceCharge,
                                offers: appSummary.offers,
                                deliveryOffer: appSummary.keetaDeliveryOffer,
                              })}
                              %) /{" "}
                              {formatter.format(
                                appSummary.serviceCharge.toFixed(2),
                              )}
                            </div>
                            <div className="monthly-expand-p">
                              <div className="monthly-expand-p-grid">
                                <div>
                                  <strong>
                                    {translations[lang].reports.monthly.body[8]}
                                  </strong>
                                  (
                                  {appSummary.completed > 0
                                    ? (
                                        appSummary.deliveryOffer /
                                        appSummary.completed
                                      ).toFixed(2)
                                    : 0}
                                  ) /{" "}
                                  {formatter.format(
                                    appSummary.deliveryOffer.toFixed(2),
                                  )}
                                </div>
                                <div>
                                  <strong>
                                    {
                                      translations[lang].reports.monthly
                                        .body[10]
                                    }
                                  </strong>
                                  (
                                  {appSummary.completed > 0
                                    ? (
                                        appSummary.offers / appSummary.completed
                                      ).toFixed(2)
                                    : 0}
                                  ) /{" "}
                                  {formatter.format(
                                    appSummary.offers.toFixed(2),
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="monthly-expand-p">
                              <strong>
                                {translations[lang].reports.monthly.body[11]}
                              </strong>
                              {formatter.format(
                                appSummary.paidCampains.toFixed(2),
                              )}
                            </div>
                          </div>
                          {has("report.approve") &&
                            (isAppApproved && !isAppApproved?.isApproved ? (
                              <div>
                                <Button
                                  variant="danger"
                                  appearance="soft"
                                  rounded
                                  icon={<ThumbsUp size={20} />}
                                  aria-label={text.approveApp}
                                  title={text.approveApp}
                                  disabled={pending}
                                  onClick={stopRowToggle(() =>
                                    approveData(appItems[0].storeName, appItems[0].date, app, storeIdsOf(appItems)),
                                  )}
                                ></Button>
                              </div>
                            ) : (
                              <div>
                                <Button
                                  variant="danger"
                                  appearance="soft"
                                  rounded
                                  icon={<ThumbsDown size={20} />}
                                  aria-label={text.disapproveApp}
                                  title={text.disapproveApp}
                                  disabled={pending}
                                  onClick={stopRowToggle(() =>
                                    disapproveData(appItems[0].storeName, appItems[0].date, app, storeIdsOf(appItems)),
                                  )}
                                ></Button>
                              </div>
                            ))}
                        </div>

                        {expandedApp === app && (
                          <div className="expand-branches">
                            {paged && <NodeState state={paged.leaf} />}
                            {Object.entries(branches).map(
                              ([branch, branchItems]) => {
                                // Calculate branch summary
                                const branchSummary = branchItems.reduce(
                                  (acc, item) => {
                                    acc.completed += parseFloat(
                                      item[MONTHLY_FIELDS2.completed] || 0,
                                    );
                                    acc.completedAmount += parseFloat(
                                      item[MONTHLY_FIELDS2.completedAmount] ||
                                        0,
                                    );
                                    acc.rejected += parseFloat(
                                      item[MONTHLY_FIELDS2.rejected] || 0,
                                    );
                                    acc.canceled += parseFloat(
                                      item[MONTHLY_FIELDS2.canceled] || 0,
                                    );
                                    acc.rejectedAmount += parseFloat(
                                      item[MONTHLY_FIELDS2.rejectedAmount] || 0,
                                    );
                                    acc.canceledAmount += parseFloat(
                                      item[MONTHLY_FIELDS2.canceledAmount] || 0,
                                    );
                                    acc.endurance += parseFloat(
                                      item[MONTHLY_FIELDS2.endurance] || 0,
                                    );
                                    acc.enduranceAmount += parseFloat(
                                      item[MONTHLY_FIELDS2.enduranceAmount] ||
                                        0,
                                    );
                                    acc.refund += parseFloat(
                                      item[MONTHLY_FIELDS2.refund] || 0,
                                    );
                                    acc.refundAmount += parseFloat(
                                      item[MONTHLY_FIELDS2.refundAmount] || 0,
                                    );
                                    acc.serviceCharge += parseFloat(
                                      item[MONTHLY_FIELDS2.serviceCharge] || 0,
                                    );
                                    acc.deliveryOffer += parseFloat(
                                      item[MONTHLY_FIELDS2.deliveryOffer] || 0,
                                    );
                                    acc.keetaDeliveryOffer +=
                                      normalizeDeliveryAppKey(item[MONTHLY_FIELDS2.app]) === "keeta"
                                        ? parseFloat(
                                            item[
                                              MONTHLY_FIELDS2.deliveryOffer
                                            ] || 0,
                                          )
                                        : 0;
                                    acc.offers += parseFloat(item.offers || 0);
                                    acc.paidCampains += parseFloat(
                                      item.paidCampains || 0,
                                    );
                                    acc.ccAmount += parseFloat(
                                      item[MONTHLY_FIELDS2.ccAmount] || 0,
                                    );
                                    acc.vat += parseFloat(
                                      item[MONTHLY_FIELDS2.vat] || 0,
                                    );
                                    acc.grandTotal += parseFloat(
                                      item[MONTHLY_FIELDS2.grandTotal] || 0,
                                    );
                                    return acc;
                                  },
                                  {
                                    completed: 0,
                                    completedAmount: 0,
                                    rejected: 0,
                                    canceled: 0,
                                    rejectedAmount: 0,
                                    canceledAmount: 0,
                                    endurance: 0,
                                    enduranceAmount: 0,
                                    refund: 0,
                                    refundAmount: 0,
                                    serviceCharge: 0,
                                    deliveryOffer: 0,
                                    keetaDeliveryOffer: 0,
                                    offers: 0,
                                    paidCampains: 0,
                                    ccAmount: 0,
                                    vat: 0,
                                    grandTotal: 0,
                                  },
                                );

                                const isApproved = branchItems.find(
                                  (a) => a.isApproved === false,
                                );
                                const editingThis =
                                  isEditing === branchItems[0]._id;

                                return (
                                  <div
                                    key={branch}
                                    className={`monthly-expand-summary-card ${
                                      can("report.monthly.branch.open")
                                        ? "branch"
                                        : ""
                                    }  ${
                                      isApproved && !isApproved?.isApproved
                                        ? "notApproved"
                                        : ""
                                    }`}
                                  >
                                    {editingThis && (
                                      <div className="monthly-expand-p all-cols-branch">
                                        <textarea
                                          aria-label={`${branch} – ${getFieldLabel(lang, "notes", AR_FIELD_LABELS)}`}
                                          className="monthly-expand-input monthly-textarea"
                                          type="text"
                                          placeholder={`${translations[lang].requests.header[7]}...`}
                                          value={editItem.notes || ""}
                                          onChange={(e) =>
                                            handleChange(
                                              "notes",
                                              e.target.value,
                                            )
                                          }
                                        />
                                      </div>
                                    )}
                                    <div className="monthly-expand-grid-col">
                                      <div className="monthly-expand-p center branch">
                                        <p>
                                          <strong>{branch}</strong>
                                        </p>
                                      </div>
                                    </div>

                                    <div className="monthly-expand-grid-col">
                                      <div className="monthly-expand-p">
                                        <strong>
                                          {
                                            translations[lang].reports.monthly
                                              .body[0]
                                          }
                                        </strong>
                                        {editingThis ? (
                                          <>
                                            <input
                                              aria-label={`${branch} – ${getFieldLabel(lang, "completed", AR_FIELD_LABELS)}`}
                                              className="monthly-expand-input"
                                              type="number"
                                              value={
                                                editItem[
                                                  MONTHLY_FIELDS2.completed
                                                ] || ""
                                              }
                                              onChange={(e) =>
                                                handleChange(
                                                  MONTHLY_FIELDS2.completed,
                                                  e.target.value,
                                                )
                                              }
                                            />
                                            <input
                                              aria-label={`${branch} – ${getFieldLabel(lang, "completedAmount", AR_FIELD_LABELS)}`}
                                              className="monthly-expand-input"
                                              type="number"
                                              value={
                                                editItem[
                                                  MONTHLY_FIELDS2
                                                    .completedAmount
                                                ] || ""
                                              }
                                              onChange={(e) =>
                                                handleChange(
                                                  MONTHLY_FIELDS2.completedAmount,
                                                  e.target.value,
                                                )
                                              }
                                            />
                                          </>
                                        ) : (
                                          ` ${formatter2.format(
                                            branchSummary.completed,
                                          )} / ${formatter.format(
                                            branchSummary.completedAmount.toFixed(
                                              2,
                                            ),
                                          )}`
                                        )}
                                      </div>
                                      <div className="monthly-expand-p">
                                        <strong>
                                          {
                                            translations[lang].reports.monthly
                                              .body[1]
                                          }
                                        </strong>
                                        {editingThis ? (
                                          <input
                                            aria-label={`${branch} – ${getFieldLabel(lang, "vat", AR_FIELD_LABELS)}`}
                                            className="monthly-expand-input"
                                            type="number"
                                            value={
                                              editItem[MONTHLY_FIELDS2.vat] ||
                                              ""
                                            }
                                            onChange={(e) =>
                                              handleChange(
                                                MONTHLY_FIELDS2.vat,
                                                e.target.value,
                                              )
                                            }
                                          />
                                        ) : (
                                          formatter.format(
                                            branchSummary.vat.toFixed(2),
                                          )
                                        )}
                                      </div>
                                      <div className="monthly-expand-p">
                                        <strong>
                                          {
                                            translations[lang].reports.monthly
                                              .body[2]
                                          }
                                        </strong>
                                        {editingThis ? (
                                          <input
                                            aria-label={`${branch} – ${getFieldLabel(lang, "grandTotal", AR_FIELD_LABELS)}`}
                                            className="monthly-expand-input"
                                            type="number"
                                            value={
                                              editItem[
                                                MONTHLY_FIELDS2.grandTotal
                                              ] || ""
                                            }
                                            onChange={(e) =>
                                              handleChange(
                                                MONTHLY_FIELDS2.grandTotal,
                                                e.target.value,
                                              )
                                            }
                                          />
                                        ) : (
                                          ` (${createGrantTotalPercent({
                                            completedAmount:
                                              branchSummary.completedAmount,
                                            grandTotal:
                                              branchSummary.grandTotal -
                                              branchSummary.paidCampains,
                                            offers: branchSummary.offers,
                                          })}%) / ${formatter.format(
                                            branchSummary.grandTotal.toFixed(
                                              2,
                                            ) -
                                              branchSummary.paidCampains.toFixed(
                                                2,
                                              ),
                                          )}`
                                        )}
                                      </div>
                                    </div>

                                    <div className="monthly-expand-grid-col">
                                      <div className="monthly-expand-p">
                                        <div
                                          className={`monthly-expand-p-grid ${
                                            editingThis
                                              ? "editing"
                                              : ""
                                          }`}
                                        >
                                          <div>
                                            <strong>
                                              {
                                                translations[lang].reports
                                                  .monthly.body[3]
                                              }
                                            </strong>
                                            {editingThis ? (
                                              <>
                                                <input
                                                  aria-label={`${branch} – ${getFieldLabel(lang, "rejected", AR_FIELD_LABELS)}`}
                                                  className="monthly-expand-input"
                                                  type="number"
                                                  value={
                                                    editItem[
                                                      MONTHLY_FIELDS2.rejected
                                                    ] || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleChange(
                                                      MONTHLY_FIELDS2.rejected,
                                                      e.target.value,
                                                    )
                                                  }
                                                />
                                                <input
                                                  aria-label={`${branch} – ${getFieldLabel(lang, "rejectedAmount", AR_FIELD_LABELS)}`}
                                                  className="monthly-expand-input"
                                                  type="number"
                                                  value={
                                                    editItem[
                                                      MONTHLY_FIELDS2
                                                        .rejectedAmount
                                                    ] || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleChange(
                                                      MONTHLY_FIELDS2.rejectedAmount,
                                                      e.target.value,
                                                    )
                                                  }
                                                />
                                              </>
                                            ) : (
                                              `${formatter2.format(
                                                branchSummary.rejected,
                                              )} / ${formatter.format(
                                                branchSummary.rejectedAmount,
                                              )}`
                                            )}
                                          </div>
                                          <div>
                                            <strong>
                                              {
                                                translations[lang].reports
                                                  .monthly.body[4]
                                              }
                                            </strong>
                                            {editingThis ? (
                                              <>
                                                <input
                                                  aria-label={`${branch} – ${getFieldLabel(lang, "canceled", AR_FIELD_LABELS)}`}
                                                  className="monthly-expand-input"
                                                  type="number"
                                                  value={
                                                    editItem[
                                                      MONTHLY_FIELDS2.canceled
                                                    ] || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleChange(
                                                      MONTHLY_FIELDS2.canceled,
                                                      e.target.value,
                                                    )
                                                  }
                                                />
                                                <input
                                                  aria-label={`${branch} – ${getFieldLabel(lang, "canceledAmount", AR_FIELD_LABELS)}`}
                                                  className="monthly-expand-input"
                                                  type="number"
                                                  value={
                                                    editItem[
                                                      MONTHLY_FIELDS2
                                                        .canceledAmount
                                                    ] || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleChange(
                                                      MONTHLY_FIELDS2.canceledAmount,
                                                      e.target.value,
                                                    )
                                                  }
                                                />
                                              </>
                                            ) : (
                                              `${formatter2.format(
                                                branchSummary.canceled,
                                              )} / ${formatter.format(
                                                branchSummary.canceledAmount,
                                              )}`
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="monthly-expand-p">
                                        <div
                                          className={`monthly-expand-p-grid ${
                                            editingThis
                                              ? "editing"
                                              : ""
                                          }`}
                                        >
                                          <div>
                                            <strong>
                                              {
                                                translations[lang].reports
                                                  .monthly.body[5]
                                              }
                                            </strong>
                                            {editingThis ? (
                                              <>
                                                <input
                                                  aria-label={`${branch} – ${getFieldLabel(lang, "endurance", AR_FIELD_LABELS)}`}
                                                  className="monthly-expand-input"
                                                  type="number"
                                                  value={
                                                    editItem[
                                                      MONTHLY_FIELDS2.endurance
                                                    ] || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleChange(
                                                      MONTHLY_FIELDS2.endurance,
                                                      e.target.value,
                                                    )
                                                  }
                                                />{" "}
                                                /
                                                <input
                                                  aria-label={`${branch} – ${getFieldLabel(lang, "enduranceAmount", AR_FIELD_LABELS)}`}
                                                  className="monthly-expand-input"
                                                  type="number"
                                                  value={
                                                    editItem[
                                                      MONTHLY_FIELDS2
                                                        .enduranceAmount
                                                    ] || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleChange(
                                                      MONTHLY_FIELDS2.enduranceAmount,
                                                      e.target.value,
                                                    )
                                                  }
                                                />
                                              </>
                                            ) : (
                                              `${formatter2.format(
                                                branchSummary.endurance,
                                              )} / ${formatter.format(
                                                branchSummary.enduranceAmount.toFixed(
                                                  2,
                                                ),
                                              )}`
                                            )}
                                          </div>
                                          <div>
                                            <strong>
                                              {
                                                translations[lang].reports
                                                  .monthly.body[6]
                                              }
                                            </strong>
                                            {editingThis ? (
                                              <>
                                                <input
                                                  aria-label={`${branch} – ${getFieldLabel(lang, "refund", AR_FIELD_LABELS)}`}
                                                  className="monthly-expand-input"
                                                  type="number"
                                                  value={
                                                    editItem[
                                                      MONTHLY_FIELDS2.refund
                                                    ] || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleChange(
                                                      MONTHLY_FIELDS2.refund,
                                                      e.target.value,
                                                    )
                                                  }
                                                />{" "}
                                                /
                                                <input
                                                  aria-label={`${branch} – ${getFieldLabel(lang, "refundAmount", AR_FIELD_LABELS)}`}
                                                  className="monthly-expand-input"
                                                  type="number"
                                                  value={
                                                    editItem[
                                                      MONTHLY_FIELDS2
                                                        .refundAmount
                                                    ] || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleChange(
                                                      MONTHLY_FIELDS2.refundAmount,
                                                      e.target.value,
                                                    )
                                                  }
                                                />
                                              </>
                                            ) : (
                                              `${formatter2.format(
                                                branchSummary.refund,
                                              )} / ${formatter.format(
                                                branchSummary.refundAmount.toFixed(
                                                  2,
                                                ),
                                              )}`
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="monthly-expand-p">
                                        <strong>
                                          {
                                            translations[lang].reports.monthly
                                              .body[9]
                                          }
                                        </strong>
                                        {editingThis ? (
                                          <input
                                            aria-label={`${branch} – ${getFieldLabel(lang, "ccAmount", AR_FIELD_LABELS)}`}
                                            className="monthly-expand-input"
                                            type="number"
                                            value={
                                              editItem[
                                                MONTHLY_FIELDS2.ccAmount
                                              ] || ""
                                            }
                                            onChange={(e) =>
                                              handleChange(
                                                MONTHLY_FIELDS2.ccAmount,
                                                e.target.value,
                                              )
                                            }
                                          />
                                        ) : (
                                          formatter.format(
                                            branchSummary.ccAmount.toFixed(2),
                                          )
                                        )}
                                      </div>
                                    </div>

                                    <div className="monthly-expand-grid-col">
                                      <div className="monthly-expand-p">
                                        <strong>
                                          {
                                            translations[lang].reports.monthly
                                              .body[7]
                                          }
                                        </strong>
                                        {editingThis ? (
                                          <input
                                            aria-label={`${branch} – ${getFieldLabel(lang, "serviceCharge", AR_FIELD_LABELS)}`}
                                            className="monthly-expand-input"
                                            type="number"
                                            value={
                                              editItem[
                                                MONTHLY_FIELDS2.serviceCharge
                                              ] || ""
                                            }
                                            onChange={(e) =>
                                              handleChange(
                                                MONTHLY_FIELDS2.serviceCharge,
                                                e.target.value,
                                              )
                                            }
                                          />
                                        ) : (
                                          `(${createServiceChargePercent({
                                            completedAmount:
                                              branchSummary.completedAmount,
                                            serviceCharge:
                                              branchSummary.serviceCharge,
                                            offers: branchSummary.offers,
                                            deliveryOffer:
                                              branchSummary.keetaDeliveryOffer,
                                          })}%) / ${formatter.format(
                                            branchSummary.serviceCharge.toFixed(
                                              2,
                                            ),
                                          )}`
                                        )}
                                      </div>
                                      <div className="monthly-expand-p">
                                        <div
                                          className={`monthly-expand-p-grid ${
                                            editingThis
                                              ? "editing"
                                              : ""
                                          }`}
                                        >
                                          <div>
                                            <strong>
                                              {
                                                translations[lang].reports
                                                  .monthly.body[8]
                                              }
                                            </strong>
                                            {editingThis ? (
                                              <input
                                                aria-label={`${branch} – ${getFieldLabel(lang, "deliveryOffer", AR_FIELD_LABELS)}`}
                                                className="monthly-expand-input"
                                                type="number"
                                                value={
                                                  editItem[
                                                    MONTHLY_FIELDS2
                                                      .deliveryOffer
                                                  ] || ""
                                                }
                                                onChange={(e) =>
                                                  handleChange(
                                                    MONTHLY_FIELDS2.deliveryOffer,
                                                    e.target.value,
                                                  )
                                                }
                                              />
                                            ) : (
                                              `(${
                                                branchSummary.completed > 0
                                                  ? (
                                                      branchSummary.deliveryOffer /
                                                      branchSummary.completed
                                                    ).toFixed(2)
                                                  : 0
                                              }) / ${formatter.format(
                                                branchSummary.deliveryOffer.toFixed(
                                                  2,
                                                ),
                                              )}`
                                            )}
                                          </div>
                                          <div>
                                            <strong>
                                              {
                                                translations[lang].reports
                                                  .monthly.body[10]
                                              }
                                            </strong>
                                            {editingThis ? (
                                              <input
                                                aria-label={`${branch} – ${getFieldLabel(lang, "offers", AR_FIELD_LABELS)}`}
                                                className="monthly-expand-input"
                                                type="number"
                                                value={editItem.offers || ""}
                                                onChange={(e) =>
                                                  handleChange(
                                                    "offers",
                                                    e.target.value,
                                                  )
                                                }
                                              />
                                            ) : (
                                              `(${
                                                branchSummary.completed > 0
                                                  ? (
                                                      branchSummary.offers /
                                                      branchSummary.completed
                                                    ).toFixed(2)
                                                  : 0
                                              }) / ${formatter.format(
                                                branchSummary.offers.toFixed(2),
                                              )}`
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="monthly-expand-p">
                                        <strong>
                                          {
                                            translations[lang].reports.monthly
                                              .body[11]
                                          }
                                        </strong>
                                        {editingThis ? (
                                          <input
                                            aria-label={`${branch} – ${getFieldLabel(lang, "paidCampains", AR_FIELD_LABELS)}`}
                                            className="monthly-expand-input"
                                            type="number"
                                            value={editItem.paidCampains || 0}
                                            onChange={(e) =>
                                              handleChange(
                                                "paidCampains",
                                                e.target.value,
                                              )
                                            }
                                          />
                                        ) : (
                                          formatter.format(
                                            branchSummary.paidCampains.toFixed(
                                              2,
                                            ),
                                          )
                                        )}
                                      </div>
                                    </div>
                                    <div className="details-expand-card-body-btns">
                                      {has("report.entry.update") &&
                                        (editingThis ? (
                                          <>
                                            <Button
                                              variant="primary"
                                              appearance="filled"
                                              disabled={pending}
                                              onClick={handleSave}
                                            >
                                              {
                                                translations[lang].financial
                                                  .save
                                              }
                                            </Button>
                                            <Button
                                              variant="danger"
                                              appearance="soft"
                                              onClick={handleCancel}
                                            >
                                              {
                                                translations[lang].financial
                                                  .cancel
                                              }
                                            </Button>
                                          </>
                                        ) : (
                                          <Button
                                            variant="primary"
                                            appearance="soft"
                                            rounded
                                            icon={<Repeat size={20} />}
                                            aria-label={`${text.edit}: ${branch}`}
                                            title={text.edit}
                                            onClick={() =>
                                              handleEdit(branchItems[0])
                                            }
                                          ></Button>
                                        ))}
                                      {has("report.entry.delete") && (
                                          <Button
                                            variant="danger"
                                            appearance=""
                                            icon={<Trash2 size={20} />}
                                            aria-label={`${text.delete}: ${branch}`}
                                            title={text.delete}
                                            onClick={() =>
                                              confirmDelete(branchItems[0]._id)
                                            }
                                          ></Button>
                                        )}
                                    </div>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      {deleteTarget && (
        <DeleteModal
          cancel={() => setDeleteTarget(null)}
          id={deleteTarget}
          handleDelete={handleConfirmedDelete}
        />
      )}
    </div>
  );
};
